// Weld ops step — used by weldment parts.
//
// Captures the list of welding operations performed on the assembly.
// Storage path: `part.weldment.weldOps[]` (WeldOp per crmTypes).
//
// One row per weld op: method (MIG/TIG/Stick/Flux-Core/Sub-Arc/Spot/Plasma/
// Oxy-acetylene/Brazing/Soldering), hours, filler (narrowed to method's
// taxonomy), wire diameter, filler cost. Structured fields land on
// methodStructured / filler / diameter; legacy freeform method/electrodeType
// stay populated alongside for backwards compatibility with v1 data.

import { NumberInput } from '@/components/NumberInput';
import type { StepProps } from '../stepTypes';
import type {
  WeldOp,
  WeldmentSpec,
  WeldMethod,
  WireDiameter,
} from '@/shared/crmTypes';
import {
  WELD_METHODS,
  WELD_FILLERS_BY_METHOD,
  WIRE_DIAMETERS,
} from '@/shared/crmTypes';

export function WeldOpsStep({ part, onChange }: StepProps) {
  const rows = part.weldment?.weldOps ?? [];

  function updateRow(i: number, patch: Partial<WeldOp>) {
    const next = rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    persist(next);
  }

  function addRow() {
    const next: WeldOp[] = [
      ...rows,
      {
        method: 'MIG',
        methodStructured: 'MIG',
        hrs: 0,
        electrodeType: '',
        fillerCost: 0,
      },
    ];
    persist(next);
  }

  function removeRow(i: number) {
    persist(rows.filter((_, idx) => idx !== i));
  }

  function persist(next: WeldOp[]) {
    const weldment: WeldmentSpec = {
      subParts: part.weldment?.subParts ?? [],
      weldOps: next,
    };
    onChange({ weldment });
  }

  return (
    <div className="space-y-3">
      {rows.length === 0 && (
        <div className="text-sm text-text-mute italic border border-dashed border-line-dark rounded p-4 text-center">
          No weld ops yet. Add one per pass/method.
        </div>
      )}

      {rows.map((r, i) => {
        // Read structured method when set, fall back to legacy string. Default
        // to MIG so the filler taxonomy lookup is always defined.
        const method: WeldMethod =
          r.methodStructured ?? (isWeldMethod(r.method) ? r.method : 'MIG');
        const fillerOptions = WELD_FILLERS_BY_METHOD[method] ?? [];
        const selectedFiller = r.filler ?? '';
        const selectedDiameter = r.diameter ?? '';

        return (
          <div key={i} className="border border-line rounded p-3 bg-bg/30 space-y-2">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <Field label={`Op #${i + 1} method`}>
                <select
                  value={method}
                  onChange={(e) => {
                    const m = e.target.value as WeldMethod;
                    updateRow(i, {
                      methodStructured: m,
                      method: m, // keep legacy string in lockstep
                      // Reset filler when method changes — taxonomies don't overlap.
                      filler: undefined,
                    });
                  }}
                  className="w-full px-2 py-1.5 border border-line-dark rounded text-sm bg-paper focus:outline-none focus:border-orange"
                >
                  {WELD_METHODS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Filler / electrode">
                <select
                  value={selectedFiller}
                  onChange={(e) => {
                    const v = e.target.value;
                    updateRow(i, {
                      filler: v === '' ? undefined : v,
                      // Keep legacy `electrodeType` populated alongside so old
                      // consumers / PDF exporters still read a value.
                      electrodeType: v,
                    });
                  }}
                  disabled={fillerOptions.length === 0}
                  className="w-full px-2 py-1.5 border border-line-dark rounded text-sm bg-paper focus:outline-none focus:border-orange disabled:bg-bg disabled:text-text-mute"
                >
                  <option value="">
                    {fillerOptions.length === 0 ? 'n/a' : '— pick filler —'}
                  </option>
                  {fillerOptions.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Diameter">
                <select
                  value={selectedDiameter}
                  onChange={(e) => {
                    const v = e.target.value;
                    updateRow(i, {
                      diameter: v === '' ? undefined : (v as WireDiameter),
                    });
                  }}
                  className="w-full px-2 py-1.5 border border-line-dark rounded text-sm bg-paper focus:outline-none focus:border-orange"
                >
                  <option value="">— pick dia —</option>
                  {WIRE_DIAMETERS.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Hours">
                <NumberInput
                  value={r.hrs}
                  onChange={(v) => updateRow(i, { hrs: v ?? 0 })}
                  step={0.5}
                  unit="hrs"
                  placeholder="1.5"
                  numpadTitle="Weld hours"
                />
              </Field>

              <Field label="Filler cost ($)">
                <NumberInput
                  value={r.fillerCost}
                  onChange={(v) => updateRow(i, { fillerCost: v })}
                  step={1}
                  unit="$"
                  placeholder="3.20"
                  numpadTitle="Filler cost"
                />
              </Field>
            </div>

            <div className="text-right">
              <button
                type="button"
                onClick={() => removeRow(i)}
                className="text-xs text-text-mute hover:text-orange py-1.5 px-2"
              >
                ✕ Remove
              </button>
            </div>
          </div>
        );
      })}

      <button
        type="button"
        onClick={addRow}
        className="w-full text-sm text-orange border border-dashed border-orange/40 rounded py-2 hover:bg-orange/5 font-semibold"
      >
        + Add weld op
      </button>
    </div>
  );
}

// Type-guard — accept legacy method strings that happen to match the new
// taxonomy so old rows render with a coherent selected value.
function isWeldMethod(s: string | undefined): s is WeldMethod {
  return !!s && (WELD_METHODS as readonly string[]).includes(s);
}

interface FieldProps {
  label: string;
  children: React.ReactNode;
}

function Field({ label, children }: FieldProps) {
  return (
    <label className="block">
      <span className="block text-[10px] font-semibold text-text-mute uppercase tracking-wide mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
